/**
 * Whether each latchable verb is held or toggled, for every mode at once.
 *
 * The same shape [lookSensitivity](./lookSensitivity.ts) has, and for the same reason: the knobs in
 * `ui/settings/knobs/` write here, every control map reads here per tick, and neither learns the
 * other exists. A copy kept on a mode would be a second answer to how the player's keyboard works,
 * and it would go stale the moment a settings screen was raised over a running game.
 *
 * Read per tick rather than captured, so a mode already running picks the change up on the next
 * frame. That matters more here than for a slider: a player switching crouch to a toggle is
 * usually doing it because the game is in front of them.
 *
 * A mode that is not in the middle of a latch when this changes sees nothing at all.
 * [verbLatches](./verbLatches.ts) drops a latch the moment its verb goes back to `hold`, which is
 * what stops a player leaving the settings screen still crouching with no key down.
 */

import type { HoldMode, LatchedVerbId } from '@/core/input/verbLatchContract';

/**
 * Held, for every verb.
 *
 * What the game has always done, and what every control legend and every tutorial line assumes. A
 * toggle is a preference, not a better default: it is the option for a player whose hand does not
 * want to sit on control for a firefight.
 */
export const DEFAULT_HOLD_MODE: HoldMode = 'hold';

/**
 * Every mode, as a lookup rather than an array.
 *
 * A record keyed by the union is exhaustive by construction, so adding a third reading of a press
 * fails to compile here until it is named.
 */
const KNOWN: Readonly<Record<HoldMode, true>> = { hold: true, toggle: true };

const modes: Record<LatchedVerbId, HoldMode> = {
  aim: DEFAULT_HOLD_MODE,
  crouch: DEFAULT_HOLD_MODE,
  sprint: DEFAULT_HOLD_MODE,
};

export function holdMode(verb: LatchedVerbId): HoldMode {
  return modes[verb];
}

export function isHoldMode(value: string): value is HoldMode {
  return Object.hasOwn(KNOWN, value);
}

/**
 * Ignores anything that is not a mode, so a stored name from a later build changes nothing.
 *
 * The same answer `hudSkinChoice.ts` gives, and for the same reason: leaving the player on the
 * reading they already had cannot make a verb unusable, where defaulting could put a key back to
 * held under a hand that had learned the toggle.
 */
export function setHoldMode(verb: LatchedVerbId, value: string): void {
  if (isHoldMode(value)) modes[verb] = value;
}
